import { analyzePricing, buildContext, computeRecipeCost } from './costing';
import { revenueOf } from './finance';
import type { AdminTenantRow, DailyRevenue, ExpenseEntry, Ingredient, MenuItem, OnboardingGoal, Recipe, TenantSettings } from './types';

export function activationPath(data: {
  ingredients: Ingredient[]; recipes: Recipe[]; menuItems: MenuItem[];
  revenues: DailyRevenue[]; expenses: ExpenseEntry[]; settings: TenantSettings;
}): OnboardingGoal | undefined {
  const months = new Set(data.revenues.filter(r => revenueOf(r) > 0).map(r => r.date.slice(0, 7)));
  if (data.expenses.some(e => (e.documentType ?? 'factura') === 'factura' && e.netAmount > 0 && months.has(e.date.slice(0, 7)))) return 'numbers';
  const ctx = buildContext(data.ingredients.filter(i => !i.pricePending && !i.priceEstimated), data.recipes);
  const recipesById = new Map(data.recipes.map(r => [r.recipeId, r]));
  for (const item of data.menuItems) {
    const recipe = recipesById.get(item.recipeId);
    if (!item.active || !recipe?.active || !recipe.components.length || item.sellingPriceGross <= 0) continue;
    try {
      const cost = computeRecipeCost(item.recipeId, ctx);
      if (cost.status === 'partial' || cost.costEstimated || cost.baseCost <= 0) continue;
      if (Number.isFinite(analyzePricing(item, cost, data.settings).grossMargin)) return 'costs';
    } catch { /* An incomplete recipe is not an activation milestone. */ }
  }
}

type GrowthRow = Pick<AdminTenantRow, 'tenantId' | 'createdAt' | 'internal' | 'founderPending' | 'commercialEvents' | 'lastCommercialEvent' | 'lastCommercialEventAt' | 'acquisition' | 'activation' | 'signupFlow' | 'firstPaidAt' | 'canceledAt' | 'subscriptionStatus'>;
export function firstPaidAt(row: GrowthRow): string | undefined {
  return row.firstPaidAt ?? row.commercialEvents?.filter(e => e.type === 'alta').map(e => e.at).sort()[0]
    ?? (row.lastCommercialEvent === 'alta' ? row.lastCommercialEventAt : undefined);
}
export function growthMetrics(rows: GrowthRow[], now = new Date()) {
  const real = rows.filter(r => !r.internal);
  const day = (s: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date(s));
  // El objetivo de activacion es mensual (Pol, 23 sep 2026): la serie diaria
  // de 30 dias se quito porque con las cohortes por mes ya sobraba.
  const mesActual = day(now.toISOString()).slice(0, 7);
  // Una activacion "historica" no tiene fecha... salvo que la cuenta se haya
  // creado este mismo mes: entonces activarse este mes era lo unico posible,
  // y contarla no es inventar nada. Sin esto, el objetivo mensual ignoraba a
  // casi toda la cohorte del mes (23 sep 2026: decia 1 cuando eran 17).
  const activadosEsteMes = real.filter(r => r.activation && (
    r.activation.activatedAt ? day(r.activation.activatedAt).startsWith(mesActual) : day(r.createdAt).startsWith(mesActual)
  )).length;
  const cohorts = [...new Set(real.map(r => r.createdAt.slice(0, 7)))].sort().reverse().map(month => {
    const cohort = real.filter(r => r.createdAt.startsWith(month));
    const activated = cohort.filter(r => r.activation);
    const days = activated.filter(r => r.activation?.activatedAt).map(r => Math.max(0, (Date.parse(r.activation!.activatedAt!) - Date.parse(r.createdAt)) / 86400000));
    return { month, total: cohort.length, activated: activated.length, pct: activated.length / cohort.length * 100,
      averageDays: days.length ? days.reduce((a, b) => a + b, 0) / days.length : null, dated: days.length };
  });
  const sources = [...new Set(real.map(r => r.acquisition?.source ?? 'Sin atribución'))].sort().map(source => {
    const accounts = real.filter(r => (r.acquisition?.source ?? 'Sin atribución') === source);
    const paid = accounts.filter(r => firstPaidAt(r)).length;
    return { source, total: accounts.length, paid, pct: paid / accounts.length * 100 };
  });
  return { activadosEsteMes, cohorts, sources, historical: real.filter(r => r.activation?.historical).length };
}

/** Suma k meses a un 'YYYY-MM'. */
const sumaMeses = (month: string, k: number): string => {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + k;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};

export interface RetentionCohort {
  /** Mes del primer pago de la cohorte (YYYY-MM). */
  month: string;
  total: number;
  /** Una celda por mes natural desde el del primer pago hasta el actual. */
  cells: { month: string; retained: number; pct: number }[];
}

/**
 * Cohortes de retencion de pago: cuentas agrupadas por el mes de su PRIMER
 * pago y cuantas seguian pagando al acabar cada mes posterior. El historial
 * comercial (altas y bajas por evento) existe desde sep 2026; de antes solo
 * consta la ULTIMA baja (`canceledAt`), asi que los meses previos son la
 * mejor aproximacion posible, no un extracto. Las fotos PCOHORT# mensuales
 * del scheduler daran la version exacta hacia delante.
 */
export function retentionCohorts(rows: GrowthRow[], now = new Date()): RetentionCohort[] {
  const mesActual = now.toISOString().slice(0, 7);
  const real = rows.filter(r => !r.internal);
  const seguiaPagando = (r: GrowthRow, month: string): boolean => {
    // La ultima alta o baja registrada hasta ese mes decide; `canceledAt` se
    // trata como una baja mas por si es anterior al historial de eventos.
    const timeline = [
      ...(r.commercialEvents ?? []).filter(e => e.type === 'alta' || e.type === 'baja'),
      ...(r.canceledAt ? [{ type: 'baja' as const, at: r.canceledAt }] : []),
    ].filter(e => e.at.slice(0, 7) <= month).sort((a, b) => a.at.localeCompare(b.at));
    const ultimo = timeline[timeline.length - 1];
    if (ultimo) return ultimo.type === 'alta';
    // Sin nada registrado hasta ese mes: en el mes actual manda el estado
    // vivo de la suscripcion; hacia atras, pagar sin baja conocida es pagar.
    if (month === mesActual) return r.subscriptionStatus === 'active' || r.subscriptionStatus === 'past_due';
    return true;
  };
  const primerPago = new Map(real.map(r => [r, firstPaidAt(r)?.slice(0, 7)] as const));
  const meses = [...new Set([...primerPago.values()].filter((m): m is string => Boolean(m) && m! <= mesActual))].sort();
  return meses.map(month => {
    const cohort = real.filter(r => primerPago.get(r) === month);
    const cells: RetentionCohort['cells'] = [];
    for (let m = month; m <= mesActual; m = sumaMeses(m, 1)) {
      const retained = cohort.filter(r => seguiaPagando(r, m)).length;
      cells.push({ month: m, retained, pct: retained / cohort.length * 100 });
    }
    return { month, total: cohort.length, cells };
  });
}
